import { useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { BarChart3, RotateCcw, Search, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getAllTicketGroupBySlug,
  getAllVenueBySlug,
  getTicketTypeBySlug,
} from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import CommonSelect from "../../../components/common/Select/CommonSelect";
import { handleError } from "../../../utils/errorHandler";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";

/** Group tickets store seat totals; divide by groupTicketCount for pack count. */
const getSoldCount = (row: any): number => {
  const groupCount = Number(row?.groupTicketCount) || 0;
  const sold = Number(row?.totalTicketsSold) || 0;
  if (row?.isGroupTicket && groupCount > 0) return sold / groupCount;
  return sold;
};

/**
 * Ticket Report — ticket types / sales (Total Bookings from event home).
 * UI matches Attendee Report v3: Background, PageHeader, stats, filter card, TablesV2.
 */
const TicketReport = () => {
  const { isNightMode } = useNightMode();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [bookingStatus, setBookingStatus] = useState<any[]>([]);
  const [originalBookingStatus, setOriginalBookingStatus] = useState<any[]>([]);
  const [selectedVenue, setSelectedVenue] = useState("0");
  const [eventVenues, setEventVenues] = useState<any[]>([]);
  const [hasGroupTickets, setHasGroupTickets] = useState(false);
  const [groupTicketList, setGroupTicketList] = useState<any[]>([]);
  const [selectedGroupSorting, setSelectedGroupSorting] = useState("0");

  const getEventVenue = async () => {
    try {
      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;
      const slugId = JSON.parse(eventData).slugId;
      const resp = await getAllVenueBySlug(slugId);
      const venueOptions = (resp || []).map((item: any) => ({
        value: String(item.venueId),
        label: item.venueName,
      }));
      setEventVenues([{ value: "0", label: "All Venues" }, ...venueOptions]);
    } catch (error) {
      handleError(error);
    }
  };

  const GroupTickets = async () => {
    const eventData = localStorage.getItem("eventData");
    if (!eventData) return;
    const groupTickets = await getAllTicketGroupBySlug(JSON.parse(eventData).slugId);
    if (groupTickets?.length > 0) {
      setHasGroupTickets(true);
      setGroupTicketList([
        { value: "0", label: "All Group Tickets" },
        ...groupTickets.map((g: any) => ({ value: String(g.id), label: g.groupName })),
      ]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (!eventData) return;
        const response = await getTicketTypeBySlug(JSON.parse(eventData).slugId);
        setBookingStatus(response || []);
        setOriginalBookingStatus(response || []);
      } catch (error) {
        console.error("Error fetching ticket report:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    getEventVenue();
    GroupTickets();
  }, []);

  const handleViewBuyers = (ticketTypeId: string, groupName: string, ticketType: string) => {
    const eventData = localStorage.getItem("eventData");
    if (!eventData) return;
    navigate(`/event/reports/ticket-buyer-details`, {
      state: { eventId: JSON.parse(eventData).eventId, ticketTypeId, groupName, ticketType },
    });
  };

  const onSelectedVenue = async (venueId: string) => {
    setIsLoading(true);
    try {
      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;
      const response = await getTicketTypeBySlug(JSON.parse(eventData).slugId);
      const filtered =
        venueId !== "0"
          ? (response || []).filter((b: any) => String(b.venueId) === venueId)
          : response || [];
      setBookingStatus(filtered);
      setOriginalBookingStatus(filtered);
      setSelectedVenue(venueId);
      setSelectedGroupSorting("0");
      setSearch("");
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Base list for search/group filters (respects current group selection)
  const getFilterBase = (groupId: string) =>
    groupId === "0"
      ? originalBookingStatus
      : originalBookingStatus.filter((item) => String(item.groupingId) === groupId);

  const applySearchFilter = (base: any[], query: string) => {
    const q = query.trim().toLowerCase();
    setBookingStatus(
      !q ? base : base.filter((row) => (row.groupName || "").toLowerCase().startsWith(q))
    );
  };

  const handleGroupSorting = (selectedId: string) => {
    setSelectedGroupSorting(selectedId);
    applySearchFilter(getFilterBase(selectedId), search);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    applySearchFilter(getFilterBase(selectedGroupSorting), value);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedGroupSorting("0");
    if (selectedVenue !== "0") onSelectedVenue("0");
    else setBookingStatus(originalBookingStatus);
  };

  const hasActiveFilters =
    Boolean(search) || selectedVenue !== "0" || selectedGroupSorting !== "0";

  // Keep prior export field names / sold-count adjustment
  const exportData = bookingStatus.map((row: any) => {
    const sold = getSoldCount(row);
    const price = parseFloat(row.price) || 0;
    return {
      groupName: row.groupName || "",
      ticketType: row.ticketType || "",
      Ticketprice: price.toFixed(2),
      TicketsSold: sold,
      isGroupTicket: row.isGroupTicket ? "Yes" : "No",
      ticketStatus: row.ticketStatus || "",
    };
  });

  const totalTicketsSold = useMemo(
    () => bookingStatus.reduce((sum, row) => sum + getSoldCount(row), 0),
    [bookingStatus]
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "groupName",
        header: "GROUP NAME",
        cell: ({ row }) => (
          <span className="fw-bold contact-name-text fs-13px">{row.original?.groupName || "—"}</span>
        ),
      },
      {
        accessorKey: "ticketType",
        header: "TICKET TYPE",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px">{row.original?.ticketType || "N/A"}</span>
        ),
      },
      {
        accessorKey: "price",
        header: "PRICE",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {(parseFloat(row.original?.price) || 0).toFixed(2)}
          </span>
        ),
      },
      {
        id: "sold",
        header: "SOLD",
        accessorFn: (row) => getSoldCount(row),
        cell: ({ row }) => {
          const item = row.original;
          // Fixed-width count so every View button lines up in one column
          return (
            <div className="ticket-sold-cell">
              <span className="ticket-sold-count font-mono contact-name-text fs-12px">
                {getSoldCount(item)}
              </span>
              <button
                type="button"
                className="contact-action-btn ticket-sold-view-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewBuyers(item.ticketTypeId, item.groupName, item.ticketType);
                }}
              >
                View
              </button>
            </div>
          );
        },
      },
      {
        id: "totalPrice",
        header: "TOTAL PRICE",
        accessorFn: (row) => (parseFloat(row?.price) || 0) * getSoldCount(row),
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">
            {((parseFloat(row.original?.price) || 0) * getSoldCount(row.original)).toFixed(2)}
          </span>
        ),
      },
      {
        id: "isGroup",
        header: "IS GROUP",
        accessorFn: (row) => (row?.isGroupTicket ? "Yes" : "No"),
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px">{row.original?.isGroupTicket ? "Yes" : "No"}</span>
        ),
      },
      {
        accessorKey: "ticketStatus",
        header: "STATUS",
        cell: ({ row }) => {
          const status = (row.original?.ticketStatus || "").toLowerCase();
          const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
          const color = status === "active" ? "text-success" : status ? "text-danger" : "contact-sub-text";
          return <span className={`fs-12px ${color}`}>{label}</span>;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />

      <PageHeader
        title="Ticket Report"
        subtitle="Review ticket types and sales, filter by venue or group, then export the list."
        icon={BarChart3}
        isLoading={isLoading}
        actions={
          <ExportToExcel data={exportData} fileName="Ticket_Report" buttonText="Export (.CSV)" />
        }
      />

      <div className="contact-stats-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TICKET TYPES</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : bookingStatus.length}{" "}
              <span className="fs-14px fw-normal contact-sub-text">types</span>
            </div>
          </div>
          <div className="contact-stat-icon pink">
            <Ticket size={22} />
          </div>
        </div>
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TICKETS SOLD</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : totalTicketsSold}{" "}
              <span className="fs-14px fw-normal contact-sub-text">sold</span>
            </div>
          </div>
          <div className="contact-stat-icon orange">
            <BarChart3 size={22} />
          </div>
        </div>
      </div>

      <div className="contact-filter-card attendee-report-filters">
        <div className="attendee-filter-grid">
          {eventVenues.length > 1 && (
            <div className="attendee-filter-field">
              <CommonSelect
                options={eventVenues}
                value={selectedVenue}
                onChange={(e) => onSelectedVenue(e.target.value)}
                label="Event Venue"
              />
            </div>
          )}

          {hasGroupTickets && (
            <div className="attendee-filter-field">
              <CommonSelect
                options={groupTicketList}
                value={selectedGroupSorting}
                onChange={(e) => handleGroupSorting(e.target.value)}
                label="Group Ticket"
              />
            </div>
          )}

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Group Name</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by group name"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="contact-action-btn" onClick={clearFilters}>
              <RotateCcw size={14} /> Clear Filters
            </button>
          </div>
        )}
      </div>

      <TablesV2
        data={bookingStatus}
        columns={columns}
        isLoading={isLoading}
        getRowId={(row, idx) => String(row.ticketTypeId || `ticket-${idx}`)}
        loadingMessage="Loading ticket report..."
        emptyMessage="No ticket types match your current filters."
        pageSize={25}
      />
    </div>
  );
};

export default TicketReport;
